import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

/**
 * Team Activity target progress.
 *
 * The period window is supplied by the caller as inclusive ISO dates, matching
 * the rest of the feature: month, quarter, FY or an arbitrary range all use the
 * same call. Progress is computed server-side from the same pure evaluator the
 * unit tests exercise, so the page never re-derives a percentage of its own.
 */

export type LodgementTargetType =
  | 'lodgement_count'
  | 'lodgement_amount'
  | 'settlement_count'
  | 'settlement_amount';

export interface TargetProgress {
  targetId: string;
  ownerId: string;
  type: LodgementTargetType;
  period: 'month' | 'quarter';
  /** Count or cents, matching the target's unit. Never mix them. */
  actual: number;
  target: number;
  percent: number;
  /** The target's effective window does not cover this period. */
  notApplicable: boolean;
}

interface ProgressResult {
  from: string;
  to: string;
  scope: 'company' | 'own';
  targets: number;
  rowsConsidered: number;
  progress: TargetProgress[];
}

export const isAmountTarget = (t: LodgementTargetType): boolean =>
  t === 'lodgement_amount' || t === 'settlement_amount';

export const TARGET_TYPE_LABELS: Record<LodgementTargetType, string> = {
  lodgement_count: 'Lodge deals',
  lodgement_amount: 'Lodged amount',
  settlement_count: 'Settled deals',
  settlement_amount: 'Settled amount',
};

export type TargetPeriod = 'month' | 'quarter';

/**
 * What the manager may send. A SUBSET of `LodgementTargetInputSchema`, which is
 * `.strict()`: any key it does not declare is rejected outright, so this type
 * exists to stop a stray field reaching the wire at all.
 *
 * The two threshold fields are separate on purpose and that is not a style
 * choice. A count and a dollar amount are not interchangeable, and one
 * polymorphic `threshold` is how a 100x error ships. The server refuses a
 * payload that sets the wrong one for the type, or both.
 */
export interface LodgementTargetDraft {
  /** Namespaced owner ref. Omit entirely to mean "the caller's own target". */
  ownerId?: string;
  type: LodgementTargetType;
  period: TargetPeriod;
  /** Count types only. Positive integer. */
  thresholdCount?: number;
  /** Amount types only. INTEGER CENTS, per the repo-wide money rule. */
  thresholdCents?: number;
  label?: string;
  effectiveFrom: string;
  effectiveTo?: string;
}

export function useLodgementTargets(
  companyId: string | undefined,
  from: string,
  to: string,
  ownerId?: string
) {
  const [data, setData] = useState<ProgressResult | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(companyId));
  const [error, setError] = useState<string | null>(null);
  const [key, setKey] = useState(0);

  useEffect(() => {
    if (!companyId || !from || !to) {
      setData(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const call = httpsCallable<Record<string, unknown>, ProgressResult>(
      functions,
      'getLodgementTargetProgress'
    );
    // Omit rather than pass undefined: the callable SDK turns an undefined
    // property into `null` on the wire. See callableParam.ts.
    call({ companyId, from, to, ...(ownerId ? { ownerId } : {}) })
      .then((res) => {
        if (!cancelled) setData(res.data ?? null);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        // Never swallow. A silent failure renders "no targets", which reads as
        // "none configured" when it may be a denied or broken call.
        console.error('[useLodgementTargets] failed', e);
        setError(e instanceof Error ? e.message : 'Failed to load targets');
        setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, from, to, ownerId, key]);

  const refresh = useCallback(() => setKey((k) => k + 1), []);

  /**
   * Create a target.
   *
   * CREATE ONLY, and deliberately so. `upsertLodgementTarget` will take an `id`
   * and edit in place, but it validates the WHOLE input, `effectiveFrom`
   * included, and no callable on this feature returns a stored target record:
   * `getLodgementTargetProgress` yields the evaluated progress
   * (targetId, type, period, actual, target, percent), never the record's
   * `effectiveFrom`, `effectiveTo` or `label`. Sending an id with a freshly
   * typed effective date would therefore silently rewrite the window a past
   * period was judged against, so the manager supersedes instead: create the
   * replacement, then retire the original. That is the model the schema itself
   * documents.
   */
  const createTarget = useCallback(
    async (draft: LodgementTargetDraft): Promise<string> => {
      if (!companyId) throw new Error('No company');
      const call = httpsCallable<Record<string, unknown>, { id: string }>(
        functions,
        'upsertLodgementTarget'
      );
      // Omit absent keys rather than passing undefined: the callable SDK encodes
      // an undefined property as `null`, and `ownerId: null` fails the owner-ref
      // regex outright. Omitting it is what tells the server "the caller's own".
      const target: Record<string, unknown> = {
        type: draft.type,
        period: draft.period,
        effectiveFrom: draft.effectiveFrom,
        isActive: true,
        ...(draft.ownerId ? { ownerId: draft.ownerId } : {}),
        ...(draft.thresholdCount != null ? { thresholdCount: draft.thresholdCount } : {}),
        ...(draft.thresholdCents != null ? { thresholdCents: draft.thresholdCents } : {}),
        ...(draft.label ? { label: draft.label } : {}),
        ...(draft.effectiveTo ? { effectiveTo: draft.effectiveTo } : {}),
      };
      const res = await call({ companyId, target });
      setKey((k) => k + 1);
      return res.data.id;
    },
    [companyId]
  );

  /**
   * Retire a target. The callable soft-deletes (`isActive: false`) rather than
   * removing the document, so the record survives; the evaluator, however,
   * treats an inactive target as not applicable to EVERY period, past ones
   * included. The manager says so on screen rather than leaving the reader to
   * discover it when an old month's bar disappears.
   */
  const retireTarget = useCallback(
    async (id: string): Promise<void> => {
      if (!companyId) throw new Error('No company');
      const call = httpsCallable<Record<string, unknown>, { id: string }>(
        functions,
        'deleteLodgementTarget'
      );
      await call({ companyId, id });
      setKey((k) => k + 1);
    },
    [companyId]
  );

  return {
    data,
    progress: data?.progress ?? [],
    loading,
    error,
    refresh,
    createTarget,
    retireTarget,
  };
}
